import React, { useState, useEffect, useMemo } from 'react'
import classNames from 'classnames'
import { isMobile } from 'react-device-detect'
import {
  useAvatarFilters,
  useFilteredAvatars,
} from 'hooks/query/aiStudio/useAvailableAvatars'
import { AvatarType, ThemeType } from 'service/aiStudio'
import { DropdownSelect } from 'components/atoms/DropdownSelect'
import { useGetThemes } from 'hooks/query/aiStudio/useGetThemes'

interface ThemeSelectionStepProps {
  selectedTheme: ThemeType[] | null
  setSelectedTheme: React.Dispatch<React.SetStateAction<ThemeType[] | []>>
}

export const ThemeSelectionStep: React.FC<ThemeSelectionStepProps> = ({
  selectedTheme,
  setSelectedTheme,
}) => {
  // 전체 아바타 조회 (한 번만 호출)
  const { data: allThemes, isLoading: isLoadingThemes } = useGetThemes()

  const handleSelectTheme = (theme: ThemeType) => {
    const findTheme = selectedTheme?.find((el) => el.id === theme.id)

    if (findTheme && selectedTheme) {
      //1. 들어온 테마가 기존에 있는 테마라면 삭제
      const updateSelectedTheme = selectedTheme?.filter(
        (el) => el.id !== theme.id,
      )
      setSelectedTheme(updateSelectedTheme)
    } else {
      //2. 1번을 했는데 없다면 추가

      setSelectedTheme((prev) => {
        return prev.length === 4 ? [...prev] : [...prev, theme]
      })
    }
  }

  return (
    <div className="flex flex-col gap-4 overflow-y-auto md:h-[500px]">
      {/* 제목 섹션 */}
      <div className="flex flex-col gap-1 md:gap-2">
        <h2 className="text-[18px] font-bold text-[#2E343D]">
          장소 · 테마 선택
        </h2>
        <p className="text-base text-[#2E343D]">
          내 옷에 잘 어울리는 장소나 촬영 테마를{' '}
          <span className="text-main">최대 4개까지 선택</span> 해보세요!
        </p>
      </div>

      {/* 필터 드롭다운 */}

      {/* 모델 선택 그리드 */}
      {isLoadingThemes ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-sm text-[#8F929B]">테마를 불러오는 중...</div>
        </div>
      ) : (
        <div
          className={classNames('gap-[6px]', {
            // 모바일: 2x2 그리드
            'grid grid-cols-2': isMobile,
            // 데스크톱: 가로 배치
            'grid grid-cols-4': !isMobile,
          })}
        >
          {allThemes && allThemes.themes.length > 0 ? (
            allThemes.themes.map((theme) => {
              const findTheme = selectedTheme?.find((el) => el.id === theme.id)
              const isSelected = findTheme ? true : false

              return (
                <div
                  key={theme.id}
                  className="flex cursor-pointer flex-col gap-2"
                  onClick={() => handleSelectTheme(theme)}
                >
                  <div
                    className={classNames(
                      'relative overflow-hidden rounded-[10px] border-4 bg-gray-100',
                      {
                        // 모바일: 168px 너비
                        'h-[299px] w-[168px]': isMobile,
                        // 데스크톱: 136px 너비
                        'h-[240px] w-[136px]': !isMobile,
                        'rounded-[14px] border-[#00B7B9]': isSelected,
                        'border-transparent': !isSelected,
                      },
                    )}
                  >
                    <img
                      src={theme.thumbnailUrl}
                      alt={theme.name}
                      className="h-full w-full object-cover"
                      onError={() => {
                        // 이미지 로드 실패시 플레이스홀더 (추후 구현)
                      }}
                    />
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    {/* 모델 이름 오버레이 */}
                    <div
                      className="absolute bottom-2 left-2 text-base font-extrabold leading-tight text-white"
                      style={{
                        textShadow: '0px 0px 4px rgba(0, 0, 0, 0.5)',
                      }}
                    >
                      {theme.name}
                    </div>
                  </div>
                </div>
              )
            })
          ) : (
            <div className="flex w-full items-center justify-center py-20">
              <div className="text-sm text-[#8F929B]">
                테마가 없습니다. 새로고침 부탁드립니다!
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
